import React, { useState, useEffect } from 'react';
import NativeDatepicker from '../../../appLibraries/NativeDatePicker/NativeDatePicker';
import Select from 'react-select';
import moment from 'moment';
import '../css/DashboardStyle.css';

const SocialMediaFilters = ({ 
    filters, 
    onFiltersChange, 
    currentUserId,
    availableResponsables = [] // Liste des responsables disponibles pour le filtre (collecteurs / staffs)
}) => {
    // Affichage de la modal de filtres
    const [showModal, setShowModal] = useState(false);

    // État local pour éditer les filtres dans la modal
    const [localFilters, setLocalFilters] = useState(filters);

    // État pour les erreurs de validation des dates
    const [dateError, setDateError] = useState('');

    // Synchroniser si les filtres globaux changent (ex: reset externe)
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleFilterChange = (key, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleDateFromChange = (newValue) => {
        setDateError(''); // Réinitialiser l'erreur
        if (newValue) {
            const dateFrom = moment(newValue);
            // Bloquer les années avant 2025
            if (dateFrom.year() < 2025) {
                setDateError('La date de début ne peut pas être antérieure à l\'année 2025');
                return;
            }
        }

        // Si une date de fin existe, vérifier que la date de début n'est pas supérieure
        if (newValue && localFilters.dateTo) {
            const dateFrom = moment(newValue);
            const dateTo = moment(localFilters.dateTo);
            
            // Si la date de début est après la date de fin, réinitialiser la date de fin
            if (dateFrom.isAfter(dateTo)) {
                setDateError('La date de début ne peut pas être supérieure à la date de fin. La date de fin a été réinitialisée.');
                setLocalFilters(prev => ({
                    ...prev,
                    dateFrom: newValue,
                    dateTo: '' // Réinitialiser la date de fin
                }));
                return;
            }
        }
        
        setLocalFilters(prev => ({
            ...prev,
            dateFrom: newValue
        }));
    };

    const handleDateToChange = (newValue) => {
        setDateError(''); // Réinitialiser l'erreur
        if (newValue) {
            const dateTo = moment(newValue);
            // Bloquer les années avant 2025
            if (dateTo.year() < 2025) {
                setDateError('La date de fin ne peut pas être antérieure à l\'année 2025');
                return;
            }
        }

        // Si une date de début existe, vérifier que la date de fin n'est pas antérieure
        if (newValue && localFilters.dateFrom) {
            const dateFrom = moment(localFilters.dateFrom);
            const dateTo = moment(newValue);
            
            // Si la date de fin est avant la date de début, empêcher la sélection
            if (dateTo.isBefore(dateFrom, 'day')) {
                setDateError('La date de fin ne peut pas être antérieure à la date de début');
                return; // Ne pas mettre à jour la date
            }
        }
        
        setLocalFilters(prev => ({
            ...prev,
            dateTo: newValue
        }));
    };

    // Gestion spécifique des statuts de prise en charge pour les rendre exclusifs
    const handleMyChargeChange = (e) => {
        const isChecked = e.target.checked;
        setLocalFilters(prev => ({
            ...prev,
            myCharge: isChecked,
            // Si je prends "à ma charge", on désactive les autres statuts
            notTaken: isChecked ? false : prev.notTaken,
            takenByOthers: isChecked ? false : prev.takenByOthers,
            takenByUser: isChecked ? null : prev.takenByUser,
        }));
    };

    const handleTakenByOthersChange = (e) => {
        const isChecked = e.target.checked;
        setLocalFilters(prev => ({
            ...prev,
            takenByOthers: isChecked,
            // Quand on coche "par d'autres", on désactive les autres statuts
            myCharge: isChecked ? false : prev.myCharge,
            notTaken: isChecked ? false : prev.notTaken,
            // Quand on décoche, on vide aussi l'utilisateur choisi
            takenByUser: isChecked ? prev.takenByUser : null,
        }));
    };

    const handleNotTakenChange = (e) => {
        const isChecked = e.target.checked;
        setLocalFilters(prev => ({
            ...prev,
            notTaken: isChecked,
            // Quand on coche "non prises en charge", on désactive les autres statuts
            myCharge: isChecked ? false : prev.myCharge,
            takenByOthers: isChecked ? false : prev.takenByOthers,
            takenByUser: isChecked ? null : prev.takenByUser,
        }));
    };

    // Rendre "Discussions non lues" et "Discussions lues" mutuellement exclusives
    const handleUnreadChange = (e) => {
        const isChecked = e.target.checked;
        setLocalFilters(prev => ({
            ...prev,
            unread: isChecked,
            read: isChecked ? false : prev.read,
        }));
    };

    const handleReadChange = (e) => {
        const isChecked = e.target.checked;
        setLocalFilters(prev => ({
            ...prev,
            read: isChecked,
            unread: isChecked ? false : prev.unread,
        }));
    };

    const applyFilters = () => {
        // Vérifier la validation des dates avant d'appliquer
        if (localFilters.dateFrom && localFilters.dateTo) {
            const dateFrom = moment(localFilters.dateFrom);
            const dateTo = moment(localFilters.dateTo);
            
            // Vérifier que la date de fin n'est pas antérieure à la date de début (comparaison au niveau du jour)
            if (dateTo.isBefore(dateFrom, 'day')) {
                setDateError('La date de fin ne peut pas être antérieure à la date de début');
                return; // Ne pas appliquer les filtres si erreur
            }
        }
        
        // Réinitialiser l'erreur et propager les filtres
        setDateError('');
        onFiltersChange(localFilters);
        setShowModal(false);
    };

    const resetFilters = () => {
        const empty = {
            dateFrom: '',
            dateTo: '',
            senderName: '',
            myCharge: false,
            notTaken: false,
            takenByOthers: false,
            takenByUser: null,
            unread: false,
            read: false
        };
        setLocalFilters(empty);
        setDateError(''); // Réinitialiser l'erreur aussi
        onFiltersChange(empty);
    };

    // Options pour le select des responsables
    const responsableOptions = availableResponsables.map(resp => ({
        value: resp.id,
        label: resp.identite || resp.name || `Utilisateur ${resp.id}`
    }));

    return (
        <>
            {/* Bouton pour ouvrir la modal de filtres */}
            <div className="mb-3 d-flex justify-content-start">
                <button
                    type="button"
                    className="btn btn-sm btn-light-primary font-weight-bold"
                    onClick={() => {
                        setDateError(''); // Réinitialiser l'erreur à l'ouverture
                        setShowModal(true);
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px'
                    }}
                >
                    <i className="fa fa-filter"></i>
                    <span>Filtres</span>
                </button>
            </div>

            {/* Modal de filtres */}
            {showModal && (
                <>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                        style={{ paddingRight: 0 }}
                    >
                        <div className="modal-dialog modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className="fa fa-filter mr-2 text-primary"></i>
                                        Filtres des discussions
                                    </h5>
                                    <button
                                        type="button"
                                        className="close"
                                        aria-label="Close"
                                        onClick={() => setShowModal(false)}
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>

                                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                    <div className="d-flex flex-column" style={{ gap: '20px' }}>
                                        {/* Filtres de dates sur la même ligne */}
                                        <div className="d-flex" style={{ gap: '15px' }}>
                                            {/* Filtre par date de début */}
                                            <div style={{ flex: 1 }}>
                                                <label className="form-label font-weight-bold mb-2" style={{ color: '#3f4254', fontSize: '14px' }}>
                                                    <i className="fa fa-calendar mr-2 text-primary"></i>
                                                    Date de début
                                                </label>
                                                <NativeDatepicker
                                                    key="date-from-picker"
                                                    value={localFilters.dateFrom || ''}
                                                    onChange={handleDateFromChange}
                                                    className="w-100"
                                                >
                                                    <div className="date-pickera" style={{ 
                                                        border: '1px solid #e4e6ef', 
                                                        borderRadius: '4px',
                                                        padding: '10px 15px',
                                                        cursor: 'pointer',
                                                        background: '#fff',
                                                        transition: 'all 0.3s'
                                                    }}>
                                                        <i className="filter-calendar-icon flaticon2-calendar-4 fs-1 mr-2 text-primary"></i>
                                                        <span style={{ color: localFilters.dateFrom ? '#3f4254' : '#a1a5b7' }}>
                                                            {localFilters.dateFrom || 'Sélectionner une date'}
                                                        </span>
                                                    </div>
                                                </NativeDatepicker>
                                            </div>

                                            {/* Filtre par date de fin */}
                                            <div style={{ flex: 1 }}>
                                                <label className="form-label font-weight-bold mb-2" style={{ color: '#3f4254', fontSize: '14px' }}>
                                                    <i className="fa fa-calendar mr-2 text-primary"></i>
                                                    Date de fin
                                                </label>
                                                <NativeDatepicker
                                                    key="date-to-picker"
                                                    value={localFilters.dateTo || ''}
                                                    onChange={handleDateToChange}
                                                    className="w-100"
                                                >
                                                    <div className="date-pickera" style={{ 
                                                        border: dateError ? '1px solid #f1416c' : '1px solid #e4e6ef', 
                                                        borderRadius: '4px',
                                                        padding: '10px 15px',
                                                        cursor: 'pointer',
                                                        background: '#fff',
                                                        transition: 'all 0.3s'
                                                    }}>
                                                        <i className="filter-calendar-icon flaticon2-calendar-4 fs-1 mr-2 text-primary"></i>
                                                        <span style={{ color: localFilters.dateTo ? '#3f4254' : '#a1a5b7' }}>
                                                            {localFilters.dateTo || 'Sélectionner une date'}
                                                        </span>
                                                    </div>
                                                </NativeDatepicker>
                                            </div>
                                        </div>
                                        {/* Message d'erreur pour les dates */}
                                        {dateError && (
                                            <div className="alert alert-danger mt-2 mb-0" style={{ fontSize: '12px', padding: '8px 12px' }}>
                                                <i className="fa fa-exclamation-triangle mr-2"></i>
                                                {dateError}
                                            </div>
                                        )}

                                        {/* Filtre par nom de l'expéditeur */}
                                        <div>
                                            <label className="form-label font-weight-bold mb-2" style={{ color: '#3f4254', fontSize: '14px' }}>
                                                <i className="fa fa-user mr-2 text-primary"></i>
                                                Nom de l'expéditeur
                                            </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Rechercher par nom..."
                                                    value={localFilters.senderName || ''}
                                                    onChange={(e) => handleFilterChange('senderName', e.target.value)}
                                                    style={{ 
                                                        border: '1px solid #e4e6ef', 
                                                        borderRadius: '4px',
                                                        padding: '10px 15px'
                                                    }}
                                                />
                                        </div>

                                        {/* Filtre par statut de prise en charge */}
                                        <div>
                                            <label className="form-label font-weight-bold mb-2" style={{ color: '#3f4254', fontSize: '14px' }}>
                                                <i className="fa fa-hand-paper mr-2 text-primary"></i>
                                                Statut de prise en charge
                                            </label>
                                            <div className="d-flex flex-column" style={{ gap: '12px', paddingLeft: '5px' }}>
                                                <label className="radio radio-primary" style={{ cursor: 'pointer', margin: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="charge-status"
                                                        checked={localFilters.myCharge === true}
                                                        onChange={handleMyChargeChange}
                                                    />
                                                    <span></span>
                                                    <span style={{ marginLeft: '8px', color: '#3f4254' }}>Discussion à ma charge</span>
                                                </label>
                                                <label className="radio radio-primary" style={{ cursor: 'pointer', margin: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="charge-status"
                                                        checked={localFilters.notTaken === true}
                                                        onChange={handleNotTakenChange}
                                                    />
                                                    <span></span>
                                                    <span style={{ marginLeft: '8px', color: '#3f4254' }}>Discussions non prises en charge</span>
                                                </label>
                                                <label className="radio radio-primary" style={{ cursor: 'pointer', margin: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="charge-status"
                                                        checked={localFilters.takenByOthers === true}
                                                        onChange={handleTakenByOthersChange}
                                                    />
                                                    <span></span>
                                                    <span style={{ marginLeft: '8px', color: '#3f4254' }}>Discussions prises en charge par d'autres utilisateurs</span>
                                                </label>
                                                {localFilters.takenByOthers && (
                                                    <div className="mt-2" style={{ marginLeft: '25px', maxWidth: '100%' }}>
                                                        <Select
                                                            options={responsableOptions}
                                                            value={responsableOptions.find(opt => opt.value === localFilters.takenByUser)}
                                                            onChange={(option) => handleFilterChange('takenByUser', option ? option.value : null)}
                                                            placeholder="Filtrer par utilisateur..."
                                                            isClearable
                                                            isSearchable
                                                            styles={{
                                                                control: (base) => ({
                                                                    ...base,
                                                                    border: '1px solid #e4e6ef',
                                                                    borderRadius: '4px',
                                                                    minHeight: '42px',
                                                                    boxShadow: 'none',
                                                                    '&:hover': {
                                                                        border: '1px solid #b5b5c3'
                                                                    }
                                                                })
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Filtre par statut de lecture */}
                                        <div>
                                            <label className="form-label font-weight-bold mb-2" style={{ color: '#3f4254', fontSize: '14px' }}>
                                                <i className="fa fa-envelope mr-2 text-primary"></i>
                                                Statut de lecture
                                            </label>
                                            <div className="d-flex flex-column" style={{ gap: '12px', paddingLeft: '5px' }}>
                                                <label className="radio radio-primary" style={{ cursor: 'pointer', margin: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="read-status"
                                                        checked={localFilters.unread === true}
                                                        onChange={handleUnreadChange}
                                                    />
                                                    <span></span>
                                                    <span style={{ marginLeft: '8px', color: '#3f4254' }}>Discussions non lues</span>
                                                </label>
                                                <label className="radio radio-primary" style={{ cursor: 'pointer', margin: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="read-status"
                                                        checked={localFilters.read === true}
                                                        onChange={handleReadChange}
                                                    />
                                                    <span></span>
                                                    <span style={{ marginLeft: '8px', color: '#3f4254' }}>Discussions lues</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={applyFilters}
                                    >
                                        Rechercher
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={resetFilters}
                                    >
                                        Réinitialiser
                                    </button>
                                    {/* <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setShowModal(false)}
                                    >
                                        Fermer
                                    </button> */}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Backdrop */}
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </>
    );
};

export default SocialMediaFilters;

